import React, { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LineBadge from '../LineBadge';

function normalizeLineName(value) {
  return String(value || '').replace(/^ligne\s*/i, '').trim().toLowerCase();
}

function TrafficDisruptionItem({ disruption, lineStyleByName }) {
  const [expanded, setExpanded] = useState(false);

  const affectedLines = useMemo(() => {
    const raw = disruption?.affected_lines || [];
    return raw.map((line) => {
      const style = lineStyleByName.get(normalizeLineName(line));
      return {
        name: line,
        color: style?.color,
        textColor: style?.textColor,
      };
    });
  }, [disruption, lineStyleByName]);

  return (
    <Alert
      severity="warning"
      icon={(
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '1.5px solid #e65100',
            color: '#e65100',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          !
        </Box>
      )}
      sx={{
        background: 'linear-gradient(135deg, #fff8e1, #fff3c4)',
        borderLeft: '5px solid',
        borderColor: 'warning.main',
        color: '#5d4037',
        borderRadius: 2,
        '& .MuiAlert-icon': { color: '#e65100' },
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <AlertTitle
              sx={{
                m: 0,
                fontWeight: 700,
                fontSize: 12,
                textTransform: 'uppercase',
                color: '#e65100',
                letterSpacing: 0.4,
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {disruption?.title || 'Info trafic'}
            </AlertTitle>
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{
              color: '#e65100',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
        {affectedLines.length > 0 ? (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            {affectedLines.map((line) => (
              <LineBadge
                key={`${disruption?.title || ''}-${line.name}`}
                lineName={line.name}
                color={line.color}
                textColor={line.textColor}
                size="small"
                sx={{ height: 22, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {disruption?.message ? (
            <Typography fontSize={12}>{disruption.message}</Typography>
          ) : null}
          {disruption?.application_period ? (
            <Typography fontSize={12}>
              <strong>Periode:</strong> {disruption.application_period}
            </Typography>
          ) : null}
          {disruption?.operating_days ? (
            <Typography fontSize={12}>
              <strong>Jours:</strong> {disruption.operating_days}
            </Typography>
          ) : null}
          {Array.isArray(disruption?.problems) && disruption.problems.length > 0 ? (
            <Typography fontSize={12}>
              <strong>Problemes:</strong> {disruption.problems.join(', ')}
            </Typography>
          ) : null}
          {disruption?.details ? (
            <Typography fontSize={12}>{disruption.details}</Typography>
          ) : null}
          {disruption?.updated_at ? (
            <Typography fontSize={11} color="rgba(93,64,55,0.8)">
              {disruption.updated_at}
            </Typography>
          ) : null}
        </Stack>
      </Collapse>
    </Alert>
  );
}

export default TrafficDisruptionItem;
